import React, { useState, useRef } from "react";
import DatePicker from "react-datepicker";
import { useFormik } from "formik";
import { Button } from "../../../../shared/Button/Button";
import { Modal } from "../../../../shared/Modal/Modal";
import { CircularProgress } from "../../../../shared/CircularProgress/CircularProgress";
import styles from "./SplitTime.module.scss";
import "react-datepicker/dist/react-datepicker.css";
import { Select } from "../../../../shared/Select/Select";
import { useSplitTime } from "./hooks/useSplitTime";

const SplitTime = ({ detail, availableTrackAs, refetchLogs }) => {
  const [showSplitModal, setShowSplitModal] = useState(false);

  const { mutate: splitTime, isLoading: isSaving } = useSplitTime(detail.id);

  const initialSelectedOption = detail.tracked_as_id
    ? { label: detail.tracked_as, value: detail.tracked_as_id }
    : null;

  const { values, setFieldValue, handleSubmit } = useFormik({
    initialValues: {
      splitTimes: [
        {
          startTime: new Date(detail.clock_in),
          endTime: new Date(detail.clock_out),
          trackedAs: initialSelectedOption,
        },
      ],
    },
    onSubmit: (values) => {
      const dto = values.splitTimes.map((split) => ({
        start_time: split.startTime.toTimeString().slice(0, 5),
        end_time: split.endTime.toTimeString().slice(0, 5),
        tracked_as: split.trackedAs ? split.trackedAs.value : null,
      }));

      splitTime(
        { slices: dto },
        {
          onSuccess: async () => {
            await refetchLogs();
            setShowSplitModal(false);
          },
        },
      );
    },
  });

  const handleHideModal = () => {
    setShowSplitModal(false);
  };

  const selectOptions = Object.values(availableTrackAs || {}).map((item) => ({
    label: item.display_name,
    value: item.id,
  }));
  const datePickerRef = useRef(null);

  const scrollToSelectedTime = () => {
    setTimeout(() => {
      const timeList = document.querySelector(".react-datepicker__time-list");
      const selectedItem = document.querySelector(
        ".react-datepicker__time-list-item--selected",
      );
      if (timeList && selectedItem) {
        timeList.scrollTop = selectedItem.offsetTop - timeList.offsetTop;
      }
    }, 100); // Delay to ensure dropdown is rendered
  };

  return (
    <>
      <Button size="tiny" onClick={() => setShowSplitModal(true)}>
        Split
      </Button>
      {showSplitModal && (
        <Modal
          isOpen
          onClose={handleHideModal}
          header={<Modal.Title>Split Time</Modal.Title>}
          footer={
            <div className={styles.buttons}>
              <Button
                size="small"
                onClick={handleSubmit}
                isDisabled={isSaving}
                leftAdornment={
                  isSaving ? (
                    <CircularProgress size="small" color="white" />
                  ) : undefined
                }
              >
                Save
              </Button>
              <Button variant="outlined" size="small" onClick={handleHideModal}>
                Cancel
              </Button>
            </div>
          }
          contentClassName={styles.modalContent}
        >
          {values.splitTimes.map((splitTime, index) => (
            <div key={index} className={styles.splitIntervalContainer}>
              <span className={styles.label}>Split Interval:</span>
              <DatePicker
                ref={datePickerRef}
                selected={splitTime.startTime}
                onChange={(date) => {
                  setFieldValue(`splitTimes.${index}.startTime`, date);
                  scrollToSelectedTime();
                }}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={1}
                minTime={new Date(detail.clock_in)}
                maxTime={new Date(detail.clock_out)}
                timeCaption="Time"
                dateFormat="hh:mm aa"
                wrapperClassName={styles.timeWrapper}
              />
              <span className={styles.label}>-</span>
              <DatePicker
                selected={splitTime.endTime}
                onChange={(date) => {
                  setFieldValue(`splitTimes.${index}.endTime`, date);
                  scrollToSelectedTime();
                }}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={1}
                minTime={new Date(detail.clock_in)}
                maxTime={new Date(detail.clock_out)}
                timeCaption="Time"
                dateFormat="hh:mm aa"
                wrapperClassName={styles.timeWrapper}
              />
              <Select
                size="small"
                className={styles.select}
                value={splitTime.trackedAs}
                options={selectOptions}
                onChange={(option) =>
                  setFieldValue(`splitTimes.${index}.trackedAs`, option)
                }
              />
              <div className={styles.addRemoveButtons}>
                <Button
                  size="small"
                  variant="text"
                  className={styles.actionButton}
                  onClick={() => {
                    const updatedSplitTimes = [
                      ...values.splitTimes,
                      {
                        startTime: new Date(detail.clock_in),
                        endTime: new Date(detail.clock_out),
                        trackedAs: null,
                      },
                    ];
                    setFieldValue("splitTimes", updatedSplitTimes);
                  }}
                >
                  +
                </Button>
                {index > 0 && (
                  <Button
                    size="small"
                    variant="text"
                    className={styles.actionButton}
                    onClick={() => {
                      const updatedSplitTimes = values.splitTimes.filter(
                        (_, i) => i !== index,
                      );
                      setFieldValue("splitTimes", updatedSplitTimes);
                    }}
                  >
                    -
                  </Button>
                )}
              </div>
            </div>
          ))}
        </Modal>
      )}
    </>
  );
};

export default SplitTime;
